import React, { FC, useEffect } from 'react'
import Grid from '@material-ui/core/Grid'
import Paper from '@material-ui/core/Paper/Paper'
import { Box, InputAdornment, makeStyles } from '@material-ui/core'
import Typography from '@material-ui/core/Typography'
import { IVendorInitValues, VendorType } from 'typescript/interfaces/vendor'
import { useFormik } from 'formik'
import TextField from '@material-ui/core/TextField'
import Button from 'UI/Button/ButtonWithPreloader'
import { useHistory, useParams } from 'react-router-dom'
import SelectComponent from 'UI/Select'
import MenuItem from '@material-ui/core/MenuItem'
import { schemaVendorForm } from 'components/VendorForm/schema'
import ReactInputMask from 'react-input-mask'
import RequiredOption from 'UI/Select/RequiredOption'
import { IUserPage, userPageInitValues } from 'typescript/interfaces/users'
import { useStates } from 'hooks/useStates'
import { useAdditionalMaterialStyle } from 'containers/MaterialUiContainer/additionalStyles'
import useValidateBankRouting, { IValidateBankRouting } from 'hooks/useValidateBankRouting'
import useDebouncedValue from 'hooks/Custom/useDebouncedValue'
import TextFieldWithPreloader from 'UI/TextField/TextFieldWithPreloader'
import DecimalTextField from 'UI/TextField/DecimalTextField'
import UppercaseTextField from 'components/UppercaseTextField'

type VendorFormValues = Omit<IVendorInitValues, 'userPage'> & {
  userPage: IUserPage & IValidateBankRouting
}

interface IVendorFormProps {
  label: string
  initValues?: IVendorInitValues
  submit: (values: IVendorInitValues) => void
  loading?: boolean
}

const defaultInitValues: IVendorInitValues = {
  comments: '',
  vendorType: '',
  ein: '',
  phone: '',
  email: '',
  nameOnCheck: '',
  userPage: userPageInitValues,
  businessName: '',
  city: '',
  street: '',
  zipCode: '',
  federalTaxId: '',
  stateId: '',
}

const useStyles = makeStyles(() => ({
  bankInfoContainer: {
    '& .MuiGrid-item': {
      paddingBottom: '2rem',
    },
  },
}))

const VendorForm: FC<IVendorFormProps> = ({ initValues = defaultInitValues, label, submit, loading }) => {
  const additionalStyle = useAdditionalMaterialStyle()
  const styles = useStyles()
  const { id } = useParams<{ id: string }>()
  const { goBack } = useHistory()
  const { handleSubmit, values, errors, touched, setFieldValue, handleChange } = useFormik<VendorFormValues>({
    initialValues: initValues,
    onSubmit: submit,
    validationSchema: schemaVendorForm(Boolean(id)),
  })
  const { data: statesData, isLoading: isLoadingStates } = useStates()

  const debouncedBankRouting = useDebouncedValue(values.userPage.bankRouting, 1000)
  const debouncedBankRoutingWire = useDebouncedValue(values.userPage.bankRoutingWire, 1000)
  const [bankInfo, isLoadingBankInfo] = useValidateBankRouting(debouncedBankRouting)
  const [bankInfoWire, isLoadingBankInfoWire] = useValidateBankRouting(debouncedBankRoutingWire)
  const isLoadingAny = loading || isLoadingBankInfo || isLoadingBankInfoWire || isLoadingStates

  useEffect(() => {
    const entityState = statesData?.data.find((state) => state.id.toString() === values.stateId)
    if (entityState) setFieldValue('userPage.entityState', entityState)
    if (bankInfo) setFieldValue('userPage.bankInfo', bankInfo)
    if (bankInfoWire) setFieldValue('userPage.bankInfoWire', bankInfoWire)
  }, [values.stateId, bankInfo, bankInfoWire, statesData?.data])

  return (
    <form onSubmit={handleSubmit}>
      <Grid className={additionalStyle.wrapper} container spacing={4}>
        <Grid item xs={6}>
          <Paper elevation={0}>
            <Box p="3rem">
              <Typography variant="h2" color="textSecondary">
                {label}
              </Typography>
              <Box mt="3rem">
                <UppercaseTextField
                  value={values.businessName}
                  onChange={handleChange}
                  error={Boolean(errors.businessName && touched.businessName)}
                  helperText={touched.businessName && errors.businessName}
                  name="businessName"
                  label={<RequiredOption label="Business Name" />}
                  variant="outlined"
                  fullWidth
                />
              </Box>
              <Box mt="3rem">
                <Grid container spacing={4}>
                  <Grid item xs={6}>
                    <SelectComponent
                      value={values.vendorType}
                      name="vendorType"
                      label={<RequiredOption label="Vendor Type" />}
                      error={Boolean(touched.vendorType && errors.vendorType)}
                      helperText={touched.vendorType && errors.vendorType}
                      onChange={(e) => {
                        setFieldValue('vendorType', e.target.value)
                      }}
                    >
                      {Object.entries(VendorType).map(([key, value]) => (
                        <MenuItem key={key} value={key}>
                          {value}
                        </MenuItem>
                      ))}
                    </SelectComponent>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      name="ein"
                      fullWidth
                      variant="outlined"
                      value={values.ein}
                      onChange={handleChange}
                      label="EIN"
                      error={Boolean(touched.ein && errors.ein)}
                      helperText={touched.ein && errors.ein}
                    />
                  </Grid>
                </Grid>
                <Box mt="2rem">
                  <Grid container spacing={4}>
                    <Grid item xs={6}>
                      <ReactInputMask name="phone" mask="(999)-999-9999" value={values.phone} onChange={handleChange}>
                        {(props: any) => (
                          <TextField
                            {...props}
                            error={Boolean(errors.phone && touched.phone)}
                            helperText={touched.phone && errors.phone}
                            label={<RequiredOption label="Phone" />}
                            variant="outlined"
                            fullWidth
                          />
                        )}
                      </ReactInputMask>
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        disabled={Boolean(id)}
                        name="email"
                        fullWidth
                        variant="outlined"
                        value={values.email}
                        onChange={handleChange}
                        label={<RequiredOption label="Email" />}
                        error={Boolean(touched.email && errors.email)}
                        helperText={touched.email && errors.email}
                      />
                    </Grid>
                  </Grid>
                </Box>
                <Box mt="2rem">
                  <Grid container spacing={4}>
                    <Grid item xs={12}>
                      <TextField
                        multiline
                        value={values.comments}
                        name="comments"
                        fullWidth
                        variant="outlined"
                        onChange={handleChange}
                        label="Comments"
                        error={Boolean(touched.comments && errors.comments)}
                        helperText={touched.comments && errors.comments}
                      />
                    </Grid>
                  </Grid>
                </Box>
                <Box mt="2rem">
                  <Grid container spacing={4}>
                    <Grid item xs={6}>
                      <UppercaseTextField
                        error={!!touched.street && !!errors.street}
                        variant="outlined"
                        helperText={touched.street && errors.street}
                        value={values.street}
                        name="street"
                        onChange={handleChange}
                        label={<RequiredOption label="Street" />}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <UppercaseTextField
                        error={!!touched.city && !!errors.city}
                        variant="outlined"
                        helperText={touched.city && errors.city}
                        value={values.city}
                        name="city"
                        onChange={handleChange}
                        label={<RequiredOption label="City" />}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </Box>
                <Box mt="2rem">
                  <Grid container spacing={4}>
                    <Grid item xs={6}>
                      <SelectComponent
                        helperText={touched.stateId && errors.stateId}
                        error={!!touched.stateId && !!errors.stateId}
                        value={values.stateId}
                        name="stateId"
                        onChange={handleChange}
                        label={<RequiredOption label="State" />}
                        fullWidth
                      >
                        {statesData?.data.map((state) => (
                          <MenuItem value={state.id}>{state.name}</MenuItem>
                        ))}
                      </SelectComponent>
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        error={!!touched.zipCode && !!errors.zipCode}
                        variant="outlined"
                        helperText={touched.zipCode && errors.zipCode}
                        value={values.zipCode}
                        name="zipCode"
                        onChange={handleChange}
                        label={<RequiredOption label="Zip Code" />}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </Box>
                <Box mt="2rem">
                  <Grid container spacing={4}>
                    <Grid item xs={6}>
                      <ReactInputMask name="federalTaxId" mask="99-9999999" value={values.federalTaxId} onChange={handleChange}>
                        {(props: any) => (
                          <TextField
                            {...props}
                            error={!!touched.federalTaxId && !!errors.federalTaxId}
                            variant="outlined"
                            helperText={touched.federalTaxId && errors.federalTaxId}
                            label={<RequiredOption label="Federal Tax ID" />}
                            fullWidth
                          />
                        )}
                      </ReactInputMask>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={6}>
          <Paper elevation={0}>
            <Box p="3rem" flexDirection="column" display="flex" height="inherit">
              <Box>
                <Typography variant="h2" color="textSecondary">
                  Bank Information
                </Typography>
                <Box mt="3rem">
                  <Grid container spacing={4} className={styles.bankInfoContainer}>
                    <Grid item xs={6}>
                      <UppercaseTextField
                        value={values.userPage.bankName}
                        name="userPage.bankName"
                        fullWidth
                        variant="outlined"
                        onChange={handleChange}
                        label={<RequiredOption label="Bank Name" />}
                        error={Boolean(touched.userPage?.bankName && errors.userPage?.bankName)}
                        helperText={touched.userPage?.bankName && errors.userPage?.bankName}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Button
                                disabled={isLoadingAny || !values.userPage?.bankInfo?.bankName}
                                size="small"
                                variant="text"
                                style={{ minWidth: 0 }}
                                onClick={() => setFieldValue('userPage.bankName', values.userPage?.bankInfo?.bankName)}
                              >
                                Autofill
                              </Button>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <ReactInputMask
                        name="userPage.bankRouting"
                        mask="999999999"
                        maskPlaceholder={null}
                        value={values.userPage.bankRouting}
                        onChange={handleChange}
                      >
                        {(props: any) => (
                          <TextFieldWithPreloader
                            variant="outlined"
                            error={!!touched.userPage?.bankRouting && !!errors.userPage?.bankRouting}
                            helperText={touched.userPage?.bankRouting && errors.userPage?.bankRouting}
                            fullWidth
                            label={<RequiredOption label="ACH Bank Routing #" />}
                            isLoading={isLoadingBankInfo || isLoadingStates}
                            isValid={!errors.userPage?.bankRouting && !!bankInfo?.bankName}
                            {...props}
                          />
                        )}
                      </ReactInputMask>
                    </Grid>
                    <Grid item xs={6}>
                      <UppercaseTextField
                        value={values.userPage.bankAccountName}
                        name="userPage.bankAccountName"
                        fullWidth
                        variant="outlined"
                        onChange={handleChange}
                        label={<RequiredOption label="Bank Account Name" />}
                        error={Boolean(touched.userPage?.bankAccountName && errors.userPage?.bankAccountName)}
                        helperText={touched.userPage?.bankAccountName && errors.userPage?.bankAccountName}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <ReactInputMask
                        name="userPage.bankRoutingWire"
                        mask="999999999"
                        maskPlaceholder={null}
                        value={values.userPage.bankRoutingWire}
                        onChange={handleChange}
                      >
                        {(props: any) => (
                          <TextFieldWithPreloader
                            variant="outlined"
                            error={!!touched.userPage?.bankRoutingWire && !!errors.userPage?.bankRoutingWire}
                            helperText={touched.userPage?.bankRoutingWire && errors.userPage?.bankRoutingWire}
                            fullWidth
                            label="Wire Bank Routing #"
                            isLoading={isLoadingBankInfoWire || isLoadingStates}
                            isValid={!errors.userPage?.bankRoutingWire && !!bankInfoWire?.bankName}
                            {...props}
                          />
                        )}
                      </ReactInputMask>
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        value={values.userPage.bankAccount}
                        name="userPage.bankAccount"
                        fullWidth
                        variant="outlined"
                        onChange={handleChange}
                        label={<RequiredOption label="Bank Account #" />}
                        error={Boolean(touched.userPage?.bankAccount && errors.userPage?.bankAccount)}
                        helperText={touched.userPage?.bankAccount && errors.userPage?.bankAccount}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <DecimalTextField
                        disabled={Boolean(id)}
                        value={values.userPage.availableBalance}
                        name="userPage.availableBalance"
                        fullWidth
                        variant="outlined"
                        onChange={handleChange}
                        label={<RequiredOption label="Starting Balance" />}
                        error={Boolean(touched.userPage?.availableBalance && errors.userPage?.availableBalance)}
                        helperText={touched.userPage?.availableBalance && errors.userPage?.availableBalance}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        value={values.nameOnCheck}
                        name="nameOnCheck"
                        fullWidth
                        variant="outlined"
                        onChange={handleChange}
                        label="Name on Voided Check"
                        error={Boolean(touched.nameOnCheck && errors.nameOnCheck)}
                        helperText={touched.nameOnCheck && errors.nameOnCheck}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Box>
              <Box display="flex" alignItems="flex-end" flex={1}>
                <Box mr={5}>
                  <Button variant="contained" color="secondary" onClick={() => goBack()} loading={isLoadingAny}>
                    Cancel
                  </Button>
                </Box>
                <Button variant="contained" color="primary" type="submit" loading={isLoadingAny}>
                  Complete
                </Button>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </form>
  )
}

export default VendorForm
